/** UI + localStorage shape for glucose readings */
export type GlucoseReadingsUi = {
  cgm: string;
  cgmDate: string;
  cgmTime: string;
  fingerPrick: string;
  fingerPrickDate: string;
  fingerPrickTime: string;
  /** Pre-meal or post-meal context for the latest finger prick reading. */
  fingerPrickMealTiming: "pre-meal" | "post-meal" | "";
  /** When API returns separate pre-meal finger prick snapshot. */
  fingerPrickPre?: GlucoseFingerSnapshot;
  /** When API returns separate post-meal finger prick snapshot. */
  fingerPrickPost?: GlucoseFingerSnapshot;
  fasting: string;
  fastingDate: string;
  fastingTime: string;
  hba1c: string;
};

export type GlucoseFingerSnapshot = {
  value: string;
  date: string;
  time: string;
};

function todayDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function currentTime(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export const EMPTY_GLUCOSE_READINGS: GlucoseReadingsUi = {
  cgm: "",
  cgmDate: todayDate(),
  cgmTime: currentTime(),
  fingerPrick: "",
  fingerPrickDate: todayDate(),
  fingerPrickTime: currentTime(),
  fingerPrickMealTiming: "",
  fasting: "",
  fastingDate: todayDate(),
  fastingTime: currentTime(),
  hba1c: "",
};

function numToUiString(v: unknown): string {
  if (v === null || v === undefined || v === "") return "";
  const n = typeof v === "number" ? v : Number(v);
  if (Number.isNaN(n)) return "";
  return String(n);
}

/** Pick first defined numeric-like value from keys on an object.
 *  Handles both flat numbers and nested `{value, entered_at}` shapes. */
function pickNum(obj: Record<string, unknown>, keys: string[]): string {
  if (!obj || typeof obj !== "object") return "";
  for (const k of keys) {
    const raw = obj[k];
    if (raw === null || raw === undefined || raw === "") continue;
    if (typeof raw === "object" && !Array.isArray(raw)) {
      const nested = raw as Record<string, unknown>;
      const s = numToUiString(nested.value);
      if (s !== "") return s;
    }
    const s = numToUiString(raw);
    if (s !== "") return s;
  }
  return "";
}

/**
 * Extract date and time from a nested `{ value, entered_at }` block or fall back to provided defaults.
 *
 * Parses date and time DIRECTLY from the ISO string to preserve the original
 * timezone as sent by the server (e.g. "2026-04-22T11:33:42+05:30" → date:"2026-04-22", time:"11:33").
 * Using `getUTCHours()` would silently convert to UTC and show the wrong local time.
 */
function extractReadingDateTime(
  block: Record<string, unknown>,
  key: string,
  fallbackDate: string,
  fallbackTime: string
): { date: string; time: string } {
  const raw = block[key];
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const nested = raw as Record<string, unknown>;
    if (typeof nested.entered_at === "string" && nested.entered_at) {
      const isoStr = nested.entered_at;
      // "2026-04-22T11:33:42+05:30" → slice directly without UTC conversion
      const datePart = isoStr.slice(0, 10);  // "2026-04-22"
      const timePart = isoStr.slice(11, 16); // "11:33"
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart) && /^\d{2}:\d{2}$/.test(timePart)) {
        return { date: datePart, time: timePart };
      }
    }
  }
  return { date: fallbackDate, time: fallbackTime };
}

function isoToDateTime(isoStr: string): { date: string; time: string } | null {
  const datePart = isoStr.slice(0, 10);
  const timePart = isoStr.slice(11, 16);
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart) && /^\d{2}:\d{2}$/.test(timePart)) {
    return { date: datePart, time: timePart };
  }
  return null;
}

function readFingerSnapshot(
  raw: unknown,
  fallbackDate: string,
  fallbackTime: string,
): GlucoseFingerSnapshot | undefined {
  if (raw === null || raw === undefined || raw === "") return undefined;

  if (typeof raw === "number" || typeof raw === "string") {
    const value = numToUiString(raw);
    if (!value) return undefined;
    return { value, date: fallbackDate, time: fallbackTime };
  }

  if (typeof raw === "object" && !Array.isArray(raw)) {
    const nested = raw as Record<string, unknown>;
    const value = numToUiString(nested.value ?? nested.mg_dl ?? nested.mgDl);
    if (!value) return undefined;
    if (typeof nested.entered_at === "string" && nested.entered_at) {
      const dt = isoToDateTime(nested.entered_at);
      if (dt) return { value, ...dt };
    }
    const dateRaw = nested.date ?? nested.reading_date;
    const timeRaw = nested.time ?? nested.reading_time;
    const date =
      typeof dateRaw === "string" && dateRaw ? dateRaw.slice(0, 10) : fallbackDate;
    const time =
      typeof timeRaw === "string" && timeRaw ? timeRaw.slice(0, 5) : fallbackTime;
    return { value, date, time };
  }

  return undefined;
}

function pickFingerSnapshot(
  block: Record<string, unknown>,
  keys: string[],
  fallbackDate: string,
  fallbackTime: string,
): GlucoseFingerSnapshot | undefined {
  for (const key of keys) {
    const snap = readFingerSnapshot(block[key], fallbackDate, fallbackTime);
    if (snap) return snap;
  }
  return undefined;
}

function readPrePostFingerSnapshots(
  block: Record<string, unknown>,
  fallbackDate: string,
  fallbackTime: string,
): { pre?: GlucoseFingerSnapshot; post?: GlucoseFingerSnapshot } {
  const pre = pickFingerSnapshot(
    block,
    [
      "pre_meal_finger_prick",
      "finger_prick_pre_meal",
      "finger_prick_pre",
      "pre_meal_glucose",
      "pre_meal_finger_prick_mg_dl",
    ],
    fallbackDate,
    fallbackTime,
  );
  const post = pickFingerSnapshot(
    block,
    [
      "post_meal_finger_prick",
      "finger_prick_post_meal",
      "finger_prick_post",
      "post_meal_glucose",
      "post_meal_finger_prick_mg_dl",
    ],
    fallbackDate,
    fallbackTime,
  );

  const nestedFp = block.finger_prick;
  if (nestedFp && typeof nestedFp === "object" && !Array.isArray(nestedFp)) {
    const fpObj = nestedFp as Record<string, unknown>;
    return {
      pre: pre ?? pickFingerSnapshot(fpObj, ["pre_meal", "pre"], fallbackDate, fallbackTime),
      post: post ?? pickFingerSnapshot(fpObj, ["post_meal", "post"], fallbackDate, fallbackTime),
    };
  }

  return { pre, post };
}

function pickMealTiming(obj: Record<string, unknown>): "pre-meal" | "post-meal" | "" {
  const raw =
    obj.meal_context ??
    obj.finger_prick_meal_timing ??
    obj.fingerPrickMealTiming ??
    obj.meal_timing ??
    obj.mealTiming;
  if (raw === "pre-meal" || raw === "pre_meal") return "pre-meal";
  if (raw === "post-meal" || raw === "post_meal") return "post-meal";
  return "";
}

export type MealContextApi = "pre_meal" | "post_meal";

export const mealTimingToMealContext = (
  timing?: "pre-meal" | "post-meal" | "",
): MealContextApi | undefined => {
  if (timing === "pre-meal") return "pre_meal";
  if (timing === "post-meal") return "post_meal";
  return undefined;
};

/**
 * Normalize API payload (several possible backend shapes) to UI fields.
 * Each of CGM, Finger Prick, Fasting gets its own date/time from the nested
 * `{ value, entered_at }` shape when available.
 */
export function glucoseReadingsFromApi(raw: unknown): GlucoseReadingsUi {
  if (!raw || typeof raw !== "object") return { ...EMPTY_GLUCOSE_READINGS };
  const root = raw as Record<string, unknown>;
  const block =
    (root.readings as Record<string, unknown>) ||
    (root.data as Record<string, unknown>) ||
    root;

  // Resolve flat fallback date/time (used when a reading has no nested entered_at)
  const dateRaw = block.reading_date ?? block.readingDate ?? block.date;
  const timeRaw = block.reading_time ?? block.readingTime ?? block.time;
  const fallbackDate = typeof dateRaw === "string" && dateRaw ? dateRaw.slice(0, 10) : todayDate();
  const fallbackTime = typeof timeRaw === "string" && timeRaw ? timeRaw.slice(0, 5) : currentTime();

  const cgmDT = extractReadingDateTime(block, "cgm", fallbackDate, fallbackTime);
  const fpDT = extractReadingDateTime(block, "finger_prick", fallbackDate, fallbackTime);
  const fastingDT = extractReadingDateTime(block, "fasting", fallbackDate, fallbackTime);
  const { pre: fingerPre, post: fingerPost } = readPrePostFingerSnapshots(
    block,
    fpDT.date,
    fpDT.time,
  );

  return {
    cgm: pickNum(block, ["cgm", "cgm_mg_dl", "cgm_value"]),
    cgmDate: cgmDT.date,
    cgmTime: cgmDT.time,
    fingerPrick: pickNum(block, [
      "finger_prick",
      "finger_prick_mg_dl",
      "fingerPrick",
      "finger_prick_value",
    ]),
    fingerPrickDate: fpDT.date,
    fingerPrickTime: fpDT.time,
    fingerPrickMealTiming: pickMealTiming(block),
    ...(fingerPre ? { fingerPrickPre: fingerPre } : {}),
    ...(fingerPost ? { fingerPrickPost: fingerPost } : {}),
    fasting: pickNum(block, [
      "fasting",
      "fasting_mg_dl",
      "fasting_glucose_mg_dl",
      "fasting_value",
    ]),
    fastingDate: fastingDT.date,
    fastingTime: fastingDT.time,
    hba1c: pickNum(block, ["hba1c", "hba1c_percent", "hba1c_value"]),
  };
}

/**
 * Partial PUT body when logging one reading from Measure page.
 * Matches backend: `{ meal_context, cgm|finger_prick|..., entered_at? }`.
 */
export type GlucoseReadingLogPayload = {
  meal_context?: MealContextApi;
  cgm?: number;
  finger_prick?: number;
  fasting?: number;
  hba1c?: number;
  /** Omit for server auto-stamp; send ISO string for manual backdate. */
  entered_at?: string;
};

export function buildGlucoseReadingLogPayload(input: {
  readingType: "finger_prick" | "cgm" | "fasting" | "hba1c";
  value: number;
  mealTiming?: "pre-meal" | "post-meal";
  /** ISO — only when user picks a custom date/time. */
  enteredAt?: string;
}): GlucoseReadingLogPayload {
  const payload: GlucoseReadingLogPayload = {};

  const mealContext = mealTimingToMealContext(input.mealTiming);
  if (mealContext) payload.meal_context = mealContext;

  switch (input.readingType) {
    case "cgm":
      payload.cgm = input.value;
      break;
    case "fasting":
      payload.fasting = input.value;
      break;
    case "hba1c":
      payload.hba1c = input.value;
      break;
    case "finger_prick":
    default:
      payload.finger_prick = input.value;
      break;
  }

  if (input.enteredAt) {
    payload.entered_at = input.enteredAt;
  }

  return payload;
}

/**
 * Backend contract: `cgm`, `finger_prick`, `fasting`, `hba1c` (numbers or null).
 * Per-reading `entered_at` timestamps sent as separate fields for backends that support them.
 * GET/PUT `/food_scanner/glucose-readings/`
 */
export type GlucoseReadingsApiPayload = {
  cgm: number | null;
  finger_prick: number | null;
  fasting: number | null;
  hba1c: number | null;
  entered_at?: string;
  cgm_entered_at?: string;
  finger_prick_entered_at?: string;
  fasting_entered_at?: string;
  meal_context?: MealContextApi;
  /** @deprecated prefer meal_context */
  finger_prick_meal_timing?: "pre-meal" | "post-meal";
};

function parseOptionalNumber(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/**
 * Build an ISO 8601 timestamp from a date string and HH:MM time string.
 * Treats the input as LOCAL browser time (no timezone suffix) so `new Date()`
 * parses it in local time; `.toISOString()` then converts to UTC with `Z` suffix.
 * This keeps the stored UTC value correct regardless of the user's timezone.
 * e.g. IST device: "2026-04-22" + "11:33" → "2026-04-22T06:03:00.000Z"
 */
function buildEnteredAt(date: string, time: string): string {
  const d = new Date(`${date}T${time || "00:00"}:00`);
  return Number.isNaN(d.getTime()) ? `${date}T${time || "00:00"}:00Z` : d.toISOString();
}

export function glucoseReadingsToApiPayload(ui: GlucoseReadingsUi): GlucoseReadingsApiPayload {
  const cgmEnteredAt = ui.cgmDate ? buildEnteredAt(ui.cgmDate, ui.cgmTime) : undefined;
  const fpEnteredAt = ui.fingerPrickDate ? buildEnteredAt(ui.fingerPrickDate, ui.fingerPrickTime) : undefined;
  const fastingEnteredAt = ui.fastingDate ? buildEnteredAt(ui.fastingDate, ui.fastingTime) : undefined;

  // Primary entered_at = first available per-reading timestamp (cgm → finger_prick → fasting)
  const primaryEnteredAt = cgmEnteredAt ?? fpEnteredAt ?? fastingEnteredAt;

  return {
    cgm: parseOptionalNumber(ui.cgm),
    finger_prick: parseOptionalNumber(ui.fingerPrick),
    fasting: parseOptionalNumber(ui.fasting),
    hba1c: parseOptionalNumber(ui.hba1c),
    ...(primaryEnteredAt ? { entered_at: primaryEnteredAt } : {}),
    ...(cgmEnteredAt ? { cgm_entered_at: cgmEnteredAt } : {}),
    ...(fpEnteredAt ? { finger_prick_entered_at: fpEnteredAt } : {}),
    ...(fastingEnteredAt ? { fasting_entered_at: fastingEnteredAt } : {}),
    ...(mealTimingToMealContext(ui.fingerPrickMealTiming)
      ? { meal_context: mealTimingToMealContext(ui.fingerPrickMealTiming) }
      : {}),
  };
}

export function glucoseReadingsUiHasAny(ui: GlucoseReadingsUi): boolean {
  return [ui.cgm, ui.fingerPrick, ui.fasting, ui.hba1c].some(
    (x) => typeof x === "string" && x.trim() !== ""
  );
}

/** Prefer API when it has any value; otherwise use fallback (e.g. localStorage). */
export function mergeGlucoseReadingsDisplay(
  api: GlucoseReadingsUi | null | undefined,
  fallback: GlucoseReadingsUi
): GlucoseReadingsUi {
  if (api && glucoseReadingsUiHasAny(api)) return api;
  return fallback;
}
